import React, { useState, useEffect, useRef } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { Box, Paper, Typography, Button, Chip, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel } from '@mui/material';

const IsotonicRegression = () => {
  const [step, setStep] = useState(0);
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  
  // Analysis state variables
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  
  // Mode detection
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;
  
  const [showTitleEditor, setShowTitleEditor] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const plotRef = useRef(null);
  
  const [plotTitle, setPlotTitle] = useState('');
  const [xAxisTitle, setXAxisTitle] = useState('');
  const [yAxisTitle, setYAxisTitle] = useState('Count');
  const [y2AxisTitle, setY2AxisTitle] = useState('Average Target');
  const [saveColName, setSaveColName] = useState('');
  
  const [formData, setFormData] = useState({
    Default_Date: null, Risk_driver: null, Target_variable: null,
    increasing: 'auto', y_min: '', y_max: '', out_of_bounds: 'nan'
  });

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  // Load saved analysis on mount
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    } else {
      fetchDatasets();
    }
  }, []);

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (results?.bin_stats && formData.Risk_driver?.value && formData.Target_variable?.value) {
      setPlotTitle(`Isotonic Regression: ${formData.Risk_driver.value} vs ${formData.Target_variable.value}`);
      setXAxisTitle(formData.Risk_driver.value);
      setSaveColName(`${formData.Risk_driver.value}_iso`);
    }
  }, [results, formData.Risk_driver, formData.Target_variable]);

  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_parameters) {
        // Restore dataset
        setSelectedDatasetId(analysis.input_parameters.dataset_id);
        setSelectedDataset({
          id: analysis.input_parameters.dataset_id,
          name: analysis.input_parameters.dataset_name,
          rows: analysis.input_parameters.rows,
          columns: analysis.input_parameters.columns
        });
        
        // Restore columns
        if (analysis.input_parameters.columns) {
          setColumns(analysis.input_parameters.columns);
        }
        
        // Restore form data
        setFormData({
          Default_Date: analysis.input_parameters.Default_Date ? { value: analysis.input_parameters.Default_Date, label: analysis.input_parameters.Default_Date } : null,
          Risk_driver: analysis.input_parameters.Risk_driver ? { value: analysis.input_parameters.Risk_driver, label: analysis.input_parameters.Risk_driver } : null,
          Target_variable: analysis.input_parameters.Target_variable ? { value: analysis.input_parameters.Target_variable, label: analysis.input_parameters.Target_variable } : null,
          increasing: analysis.input_parameters.increasing || 'auto',
          y_min: analysis.input_parameters.y_min || '',
          y_max: analysis.input_parameters.y_max || '',
          out_of_bounds: analysis.input_parameters.out_of_bounds || 'nan'
        });
        
        // Restore plot titles
        if (analysis.input_parameters.plotTitle) setPlotTitle(analysis.input_parameters.plotTitle);
        if (analysis.input_parameters.xAxisTitle) setXAxisTitle(analysis.input_parameters.xAxisTitle);
        if (analysis.input_parameters.yAxisTitle) setYAxisTitle(analysis.input_parameters.yAxisTitle);
        if (analysis.input_parameters.y2AxisTitle) setY2AxisTitle(analysis.input_parameters.y2AxisTitle);
        if (analysis.input_parameters.saveColName) setSaveColName(analysis.input_parameters.saveColName);
      }
      
      if (analysis.output_result) {
        setResults(analysis.output_result);
        setStep(2);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(1);
        toast.success('Analysis loaded. Please run the analysis to see results.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
      fetchDatasets();
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        size_mb: (ds.file_size / 1e6).toFixed(2),
        rows: ds.row_count,
        columns: ds.column_count,
        exists: true
      }));
      setDatasets(datasets);
    } catch (err) {
      toast.error('Failed to load datasets');
    }
  };

  const handleSelectDataset = async () => {
    if (!selectedDatasetId) return;
    setLoading(true);
    try {
      const res = await api.post('/api/isotonic-regression/select-dataset', { dataset_id: parseInt(selectedDatasetId) });
      const selectedDs = datasets.find(ds => ds.id == selectedDatasetId);
      setSelectedDataset({
        id: res.data.table_id,
        name: selectedDs?.name || 'Unknown',
        rows: res.data.rows,
        columns: res.data.columns
      });
      setColumns(res.data.columns || []);
      setResults(null);
      setStep(1);
      toast.success(`Loaded: ${res.data.rows?.toLocaleString()} rows`);
    } catch (err) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveParameters = async () => {
    if (!formData.Risk_driver || !formData.Target_variable) {
      toast.error('Risk Driver and Target Variable are required');
      return;
    }
    setAnalyzing(true);
    try {
      await api.post('/api/isotonic-regression/step0', {
        Risk_driver: formData.Risk_driver.value,
        Target_variable: formData.Target_variable.value,
        Default_Date: formData.Default_Date?.value || '',
        increasing: formData.increasing,
        y_min: formData.y_min,
        y_max: formData.y_max,
        out_of_bounds: formData.out_of_bounds
      });
      toast.success('Parameters saved');
      setStep(2);
      await handleRunAnalysis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRunAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post('/api/isotonic-regression/step1', {
        Risk_driver: formData.Risk_driver.value,
        Target_variable: formData.Target_variable.value,
        Default_Date: formData.Default_Date?.value || '',
        increasing: formData.increasing,
        y_min: formData.y_min,
        y_max: formData.y_max,
        out_of_bounds: formData.out_of_bounds
      });
      setResults(res.data.results);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveColumn = async () => {
    if (!saveColName.trim()) {
      toast.error('Enter a column name');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/isotonic-regression/step2', { new_column_name: saveColName.trim() });
      toast.success(`Saved as '${saveColName}'`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.post('/api/isotonic-regression/export-with-titles', {
        plotTitle, xAxisTitle, yAxisTitle, y2AxisTitle
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'isotonic_regression.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleSaveAnalysis = async () => {
    if (!selectedDataset) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    if (!results) {
      toast.error('No results to save. Please run the analysis first.');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'isotonic_regression',
        description: analysisDescription,
        input_parameters: {
          dataset_id: selectedDataset.id,
          dataset_name: selectedDataset.name,
          rows: selectedDataset.rows,
          columns: selectedDataset.columns,
          Risk_driver: formData.Risk_driver?.value || '',
          Target_variable: formData.Target_variable?.value || '',
          Default_Date: formData.Default_Date?.value || '',
          increasing: formData.increasing,
          y_min: formData.y_min,
          y_max: formData.y_max,
          out_of_bounds: formData.out_of_bounds,
          plotTitle: plotTitle,
          xAxisTitle: xAxisTitle,
          yAxisTitle: yAxisTitle,
          y2AxisTitle: y2AxisTitle,
          saveColName: saveColName
        },
        output_result: results,
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses/', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, {
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    if (!savedAnalysisId) {
      toast.error('Please save the analysis first');
      return;
    }
    
    setSavingComment(true);
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added');
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSavingComment(false);
    }
  };

  const renderPlot = () => {
    if (!results?.bin_stats || !plotRef.current || !window.Plotly) return;
    const binStats = results.bin_stats;
    const categories = binStats.map(d => d.Bin);
    const counts = binStats.map(d => d.Count);
    const avgTargets = binStats.map(d => d.Avg_Target);
    
    const trace1 = { x: categories, y: counts, type: 'bar', name: 'Count', marker: { color: '#2ecc71', opacity: 0.7 }, yaxis: 'y' };
    const trace2 = { x: categories, y: avgTargets, type: 'scatter', mode: 'lines+markers', name: y2AxisTitle || 'Average Target', line: { color: '#e74c3c', width: 3 }, marker: { size: 8, color: '#e74c3c' }, yaxis: 'y2' };
    
    const layout = {
      title: { text: plotTitle || `Isotonic Regression: ${formData.Risk_driver?.value} vs ${formData.Target_variable?.value}`, font: { size: 16 } },
      xaxis: { title: xAxisTitle || formData.Risk_driver?.value || '', tickangle: -45 },
      yaxis: { title: yAxisTitle || 'Count', side: 'left' },
      yaxis2: { title: y2AxisTitle || 'Average Target', overlaying: 'y', side: 'right' },
      template: 'plotly_white', margin: { b: 100, r: 80 }, height: 500,
      legend: { orientation: 'h', y: 1.15 }
    };
    window.Plotly.newPlot(plotRef.current, [trace1, trace2], layout, { responsive: true });
  };

  useEffect(() => {
    if (results?.bin_stats && plotRef.current) {
      renderPlot();
    }
  }, [results, plotTitle, xAxisTitle, yAxisTitle, y2AxisTitle]);

  const stepTabs = ['Select', 'Review', 'Results'];
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#2c3e50' };
  const gcs = { padding: '16px', background: 'white' };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px' };

  if (isLoadingAnalysis) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading analysis...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', marginBottom: '16px', color: '#2c3e50' }}>Isotonic Regression</h1>
      
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
        {stepTabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => {
              if (idx === 0) setStep(0);
              else if (idx === 1 && selectedDataset) setStep(1);
              else if (idx === 2 && results) setStep(2);
            }}
            style={{
              padding: '6px 12px',
              fontSize: '11px',
              background: step === idx ? '#1abc9c' : 'transparent',
              color: step === idx ? 'white' : '#7f8c8d',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Step 0: Select Dataset */}
      {step === 0 && (
        <div style={gs}>
          <div style={ghs}>📊 Select Dataset</div>
          <div style={gcs}>
            {selectedDataset && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                background: '#e8f8f5', 
                borderRadius: '8px',
                borderLeft: '4px solid #1abc9c'
              }}>
                <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
                  📌 Currently selected dataset:
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
                  {selectedDataset.name}
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                  {selectedDataset.rows?.toLocaleString()} rows • {selectedDataset.columns?.length || 0} columns
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <select
                value={selectedDatasetId}
                onChange={(e) => {
                  setSelectedDatasetId(e.target.value);
                  setSelectedDataset(null);
                  setColumns([]);
                }}
                disabled={isViewMode}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}
              >
                <option value="">-- Select a dataset --</option>
                {datasets.map((ds) => (
                  <option key={ds.id} value={ds.id}>
                    {ds.name} ({ds.rows?.toLocaleString()} rows)
                  </option>
                ))}
              </select>
              <button
                onClick={handleSelectDataset}
                disabled={!selectedDatasetId || loading || isViewMode}
                style={{
                  padding: '12px 24px',
                  background: loading || isViewMode ? '#95a5a6' : '#1abc9c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading || isViewMode ? 'not-allowed' : 'pointer',
                  fontSize: '13px'
                }}
              >
                {loading ? 'Loading...' : 'Load Dataset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Parameters */}
      {step === 1 && selectedDataset && (
        <div>
          <div style={gs}>
            <div style={ghs}>📋 Dataset Summary</div>
            <div style={gcs}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Chip label={`${selectedDataset.rows?.toLocaleString()} rows`} sx={{ background: '#ecf0f1' }} />
                <Chip label={`${selectedDataset.columns?.length || 0} columns`} sx={{ background: '#ecf0f1' }} />
              </div>
            </div>
          </div>

          <div style={gs}>
            <div style={ghs}>⚙️ Parameters</div>
            <div style={gcs}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Risk Driver *</label>
                  <Select 
                    options={columnOptions} 
                    value={formData.Risk_driver} 
                    onChange={v => setFormData({...formData, Risk_driver: v})} 
                    styles={{ control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px' }) }}
                    menuPortalTarget={document.body} 
                    placeholder="Select risk driver..."
                    isDisabled={isViewMode}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Target Variable *</label>
                  <Select 
                    options={columnOptions} 
                    value={formData.Target_variable} 
                    onChange={v => setFormData({...formData, Target_variable: v})} 
                    styles={{ control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px' }) }}
                    menuPortalTarget={document.body} 
                    placeholder="Select target variable..."
                    isDisabled={isViewMode}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Claims Date (optional)</label>
                  <Select 
                    options={columnOptions} 
                    value={formData.Default_Date} 
                    onChange={v => setFormData({...formData, Default_Date: v})} 
                    isClearable 
                    styles={{ control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px' }) }}
                    menuPortalTarget={document.body} 
                    placeholder="Select date column..."
                    isDisabled={isViewMode}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Options</label>
                    <button 
                      type="button" 
                      onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      disabled={isViewMode}
                      style={{ 
                        width: '100%', padding: '10px 12px', fontSize: '13px', 
                        background: showAdvancedOptions ? '#1abc9c' : '#f8f9fa', 
                        color: showAdvancedOptions ? 'white' : '#7f8c8d', 
                        border: '1px solid #ddd', borderRadius: '6px', 
                        cursor: isViewMode ? 'not-allowed' : 'pointer',
                        opacity: isViewMode ? 0.5 : 1
                      }}>
                      {showAdvancedOptions ? '✓ Advanced Options Open' : '⚙️ Advanced Options'}
                    </button>
                  </div>
                </div>
              </div>

              {showAdvancedOptions && (
                <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '6px', marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Increasing</label>
                    <select 
                      value={formData.increasing} 
                      onChange={(e) => setFormData({...formData, increasing: e.target.value})} 
                      style={inputStyle}
                      disabled={isViewMode}
                    >
                      <option value="auto">Auto (detect)</option>
                      <option value="ascending">Ascending</option>
                      <option value="descending">Descending</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Out of Bounds</label>
                    <select 
                      value={formData.out_of_bounds} 
                      onChange={(e) => setFormData({...formData, out_of_bounds: e.target.value})} 
                      style={inputStyle}
                      disabled={isViewMode}
                    >
                      <option value="nan">nan (return NaN)</option>
                      <option value="clip">clip (bound)</option>
                      <option value="raise">raise (error)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Y Min</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      value={formData.y_min} 
                      onChange={(e) => setFormData({...formData, y_min: e.target.value})} 
                      placeholder="Auto" 
                      style={inputStyle}
                      disabled={isViewMode}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Y Max</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      value={formData.y_max} 
                      onChange={(e) => setFormData({...formData, y_max: e.target.value})} 
                      placeholder="Auto" 
                      style={inputStyle}
                      disabled={isViewMode}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setStep(0)}
                  disabled={isViewMode}
                  style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  ← Back
                </button>
                <button
                  onClick={handleSaveParameters}
                  disabled={analyzing || isViewMode}
                  style={{
                    padding: '10px 24px',
                    background: analyzing || isViewMode ? '#95a5a6' : '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: analyzing || isViewMode ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {analyzing ? '🔍 Running...' : '🚀 Run Isotonic Regression'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Results */}
      {step === 2 && results && (
        <div>
          {/* gAUC Card */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <Paper sx={{ textAlign: 'center', padding: '16px 32px', border: '2px solid #2ecc71' }}>
              <Typography variant="h3" fontWeight={700} sx={{ color: '#2ecc71' }}>{results.gauc_value}</Typography>
              <Typography variant="caption" color="textSecondary">gAUC (Somers' D)</Typography>
            </Paper>
          </div>

          {/* Chart */}
          <div style={gs}>
            <div style={{ ...ghs, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📈 Chart</span>
              <button 
                onClick={() => setShowTitleEditor(!showTitleEditor)}
                disabled={isViewMode}
                style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}>
                {showTitleEditor ? '✕ Close' : '✏️ Edit Titles'}
              </button>
            </div>
            <div style={gcs}>
              {showTitleEditor && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '12px', background: '#f9f9f9', borderRadius: '6px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500 }}>Chart Title</label>
                    <input type="text" value={plotTitle} onChange={e => setPlotTitle(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500 }}>X Axis</label>
                    <input type="text" value={xAxisTitle} onChange={e => setXAxisTitle(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500 }}>Y Axis (Count)</label>
                    <input type="text" value={yAxisTitle} onChange={e => setYAxisTitle(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500 }}>Y Axis (Target)</label>
                    <input type="text" value={y2AxisTitle} onChange={e => setY2AxisTitle(e.target.value)} style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
                    <button onClick={() => { renderPlot(); toast.success('Chart updated'); setShowTitleEditor(false); }}
                      style={{ padding: '6px 16px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Apply</button>
                    <button onClick={() => { setPlotTitle(`Isotonic Regression: ${formData.Risk_driver?.value} vs ${formData.Target_variable?.value}`); setXAxisTitle(formData.Risk_driver?.value || ''); setYAxisTitle('Count'); setY2AxisTitle('Average Target'); }}
                      style={{ padding: '6px 16px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Reset</button>
                  </div>
                </div>
              )}
              <div ref={plotRef} style={{ width: '100%', minHeight: '500px' }}></div>
            </div>
          </div>

          {/* Table */}
          <div style={gs}>
            <div style={ghs}>📊 Bin Statistics</div>
            <div style={{ ...gcs, padding: 0 }}>
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#2c3e50', color: 'white', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Bin</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Count</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Avg Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.bin_stats?.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{row.Bin}</td>
                        <td style={{ padding: '6px 12px' }}>{row.Count?.toLocaleString()}</td>
                        <td style={{ padding: '6px 12px' }}>{row.Avg_Target?.toFixed(6)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div style={{ ...gs, marginTop: '20px' }}>
            <div 
              style={{ 
                ...ghs, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
            >
              <span>💬 Comments & Discussions ({comments.length})</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {isCommentsExpanded ? '−' : '+'}
              </span>
            </div>
            
            {isCommentsExpanded && (
              <div style={gcs}>
                <div style={{ maxHeight: '200px', overflow: 'auto', marginBottom: '16px' }}>
                  {comments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                      No comments yet. Be the first to add a comment.
                    </Typography>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '10px 12px',
                          marginBottom: '8px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: '3px solid #2ecc71'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                            {comment.user_email || comment.user_id || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {isEditMode && savedAnalysisId && (
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment or question..."
                      style={{
                        flex: 1,
                        padding: '10px',
                        fontSize: '13px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        resize: 'vertical',
                        minHeight: '60px',
                        fontFamily: 'inherit'
                      }}
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={savingComment || !newComment.trim()}
                      style={{
                        padding: '8px 20px',
                        background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#2ecc71',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        height: 'fit-content'
                      }}
                    >
                      {savingComment ? 'Sending...' : 'Add Comment'}
                    </button>
                  </div>
                )}
                {(!savedAnalysisId || isViewMode) && (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', pt: 1 }}>
                    {!savedAnalysisId ? '💬 Save the analysis first to start commenting' : '💬 Switch to Modify mode to add comments'}
                  </Typography>
                )}
              </div>
            )}
          </div>

          {/* Professional Action Area */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4 }}>Column Name</label>
                  <input 
                    type="text" 
                    value={saveColName} 
                    onChange={e => setSaveColName(e.target.value)}
                    style={{ padding: '8px 12px', fontSize: 12, border: '1px solid #ddd', borderRadius: 6, width: 180 }}
                    disabled={isViewMode}
                  />
                </div>
                <button 
                  onClick={handleSaveColumn} 
                  disabled={loading || isViewMode}
                  style={{ 
                    padding: '8px 20px', 
                    background: loading || isViewMode ? '#95a5a6' : '#27ae60', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: 6, 
                    cursor: loading || isViewMode ? 'not-allowed' : 'pointer', 
                    fontSize: 13, 
                    whiteSpace: 'nowrap',
                    marginBottom: '0px'
                  }}>
                  {loading ? 'Saving...' : '💾 Save to Dataset'}
                </button>
              </div>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <MuiSelect
                  value={analysisStatus}
                  onChange={(e) => setAnalysisStatus(e.target.value)}
                  label="Status"
                  disabled={isViewMode}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="validated">Validated</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </MuiSelect>
              </FormControl>
            </div>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={analysisDescription}
              onChange={(e) => setAnalysisDescription(e.target.value)}
              placeholder="Describe the purpose, methodology, or key findings of this isotonic regression..."
              disabled={isViewMode}
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                disabled={isViewMode}
                size="small"
              >
                ← Back
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => { 
                  setStep(0); 
                  setSelectedDataset(null); 
                  setSelectedDatasetId('');
                  setColumns([]);
                  setResults(null); 
                  setAnalysisStatus('draft'); 
                  setAnalysisDescription(''); 
                  setSavedAnalysisId(null); 
                  setComments([]);
                  setFormData({
                    Default_Date: null, Risk_driver: null, Target_variable: null,
                    increasing: 'auto', y_min: '', y_max: '', out_of_bounds: 'nan'
                  });
                  setPlotTitle('');
                  setXAxisTitle('');
                  setYAxisTitle('Count');
                  setY2AxisTitle('Average Target');
                  setSaveColName('');
                  setShowAdvancedOptions(false);
                }}
                disabled={isViewMode}
                size="small"
              >
                New Analysis
              </Button>
              
              {isEditMode && savedAnalysisId && (
                <Button
                  variant="outlined"
                  onClick={handleUpdateMetadata}
                  disabled={updatingMetadata}
                  size="small"
                >
                  {updatingMetadata ? 'Updating...' : 'Update Metadata'}
                </Button>
              )}
              
              <Button
                variant="contained"
                onClick={handleExport}
                size="small"
                sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
              >
                📥 Export
              </Button>
              
              {isEditMode && (
                <Button
                  variant="contained"
                  onClick={handleSaveAnalysis}
                  disabled={!results || loading}
                  size="small"
                  sx={{ bgcolor: results && !loading ? '#2ecc71' : '#95a5a6', '&:hover': { bgcolor: '#27ae60' } }}
                >
                  {loading ? 'Saving...' : '💾 Save Analysis'}
                </Button>
              )}
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default IsotonicRegression;